import { useMemo, useState } from 'react';
import Modal from '../../Atoms/Modal/Modal';
import Buttons from '../../Atoms/Buttons/Buttons';
import LoaderButton from '../Loader/LoaderButton';
import styles from './ContactSettings.module.scss';

/**
 * Add or edit one contact recipient.
 *
 * One dialog for both because the entity has one field difference between
 * modes — see the rename warning below. A second component would duplicate
 * the validation and then drift from it.
 *
 * ⚠ `mobile` IS AHEAD OF THE BACKEND. The API does not persist it yet — see
 * the note on `Hooks/ContactSettings.js`. It is collected and sent regardless,
 * per the backend team's confirmed spec (mandatory, digits and a leading `+`
 * only), so the field starts working the day they deploy it with no further
 * client change. Until then a save still succeeds; the number is simply not
 * stored server-side.
 *
 * STRUCTURE — header / body / footer, which is what keeps a dialog legible at
 * any size: the title and close stay put, only the fields scroll, and the
 * actions stay reachable. The shared `Modal` atom supplies the portal, the
 * backdrop and the Escape handler; its own close control is an `<img onClick>`,
 * neither focusable nor keyboard-operable, so it is hidden and a real `<button>`
 * takes its place — the same treatment `MessageDetailsModal` gives it.
 *
 * VALIDATION IS CLIENT-SIDE FIRST, SERVER-SIDE AUTHORITATIVE. The backend
 * rejects an empty title, an empty e-mail, a malformed e-mail and a duplicate
 * address, each as HTTP 400 with `errors` keyed by PascalCase field name. The
 * checks here catch the first three before a round trip, and `serverErrors`
 * places whatever the server sends — the duplicate especially, which cannot be
 * known locally — under the field it belongs to rather than in a toast that
 * disappears while the operator is still looking for what to fix.
 */

// Deliberately permissive: the server is the authority on what it accepts, and
// a client regex stricter than the server rejects addresses the system would
// have taken. This only catches the obvious typo before a round trip.
const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

/**
 * Mobile: an optional leading `+`, then 6–15 digits, nothing else — per the
 * backend team, that is the whole rule. Not the public website's stricter
 * E.164 check (`factory/Data.jsx` there uses `libphonenumber-js` against a
 * real country list); this is a CMS operator typing a switchboard number, not
 * a visitor's personal one, and a client rule stricter than the server's would
 * reject numbers the backend accepts.
 */
const MOBILE_PATTERN = /^\+?\d{6,15}$/;

/**
 * Strips anything that is not a digit or `+` as the operator types, rather
 * than accepting a stray character and rejecting it on blur. The field can
 * then only ever hold characters `MOBILE_PATTERN` allows.
 */
const sanitiseMobile = (value) => String(value ?? '').replace(/[^\d+]/g, '');

/**
 * The backend's validation sentences, in Arabic.
 *
 * Contract §7 makes `error` the text shown to the user, and normally that is
 * right — the server knows why it refused. But this API answers in English
 * ("This email is already in the allowed list") into a CMS that is Arabic
 * throughout, and the most common refusal by far is the duplicate, which an
 * operator hits simply by re-adding a department that is already there.
 *
 * DELIBERATELY A CLOSED, EXACT-MATCH MAP with a fallback to the server's own
 * words. It cannot drift into being wrong: an unrecognised sentence, or a
 * reworded one, shows exactly what the server said. The right fix is for the
 * backend to localise, or to send a stable `errorCode` to switch on — item on
 * `DPA-FE/docs/APICall/DPA-CONFORMANCE.md`. Until then this covers the three
 * sentences measured on 2026-09-22.
 */
const SERVER_MESSAGES_AR = {
  'This email is already in the allowed list': 'هذا البريد الإلكتروني مُضاف بالفعل إلى القائمة',
  'Enter a valid email address': 'أدخل بريدًا إلكترونيًا صحيحًا',
  "'Email' must not be empty.": 'البريد الإلكتروني مطلوب',
  "'Title' must not be empty.": 'اسم الجهة مطلوب',
  // Not yet observed — `mobile` isn't deployed on the backend yet, so these
  // are the two sentences its existing FluentValidation style implies
  // (`'Email' must not be empty.` above is the same validator, same wording).
  // Verify and correct against the real response once the field ships.
  "'Mobile' must not be empty.": 'رقم الهاتف مطلوب',
};

const localise = (text) => (text ? (SERVER_MESSAGES_AR[text] ?? text) : text);

const ContactSettingFormModal = ({
  /** The row being edited, or `null` to add a new one. */
  setting = null,
  isSaving = false,
  /** `{ Email: [...], Title: [...] }` straight off the failed response. */
  serverErrors = null,
  serverMessage = '',
  onSubmit,
  onClose,
}) => {
  const isEdit = !!setting?.email;

  const [values, setValues] = useState(() => ({
    email: setting?.email ?? '',
    title: setting?.title ?? '',
    mobile: setting?.mobile ?? '',
    // A new recipient defaults to receiving the copy and being live: adding one
    // that does nothing until two more switches are found is a trap.
    isEmailSentRequired: setting ? !!setting.isEmailSentRequired : true,
    isActive: setting ? !!setting.isActive : true,
  }));
  const [touched, setTouched] = useState(false);

  const errors = useMemo(() => {
    const next = {};
    if (!values.email.trim()) next.email = 'البريد الإلكتروني مطلوب';
    else if (!EMAIL_PATTERN.test(values.email.trim()))
      next.email = 'أدخل بريدًا إلكترونيًا صحيحًا';
    if (!values.title.trim()) next.title = 'اسم الجهة مطلوب';
    if (!values.mobile.trim()) next.mobile = 'رقم الهاتف مطلوب';
    else if (!MOBILE_PATTERN.test(values.mobile.trim()))
      next.mobile = 'أدخل رقم هاتف صحيحًا (أرقام و+ فقط)';
    return next;
  }, [values]);

  // Client errors appear on submit, not while the first character is typed.
  // A server error stands until the next attempt replaces it.
  const errorFor = (field) =>
    (touched ? errors[field] : undefined) ??
    // `Email` / `Title` — the server keys its validation map in PascalCase.
    localise(serverErrors?.[field.charAt(0).toUpperCase() + field.slice(1)]?.[0]);

  const setField = (patch) => setValues((current) => ({ ...current, ...patch }));

  /**
   * The address is the primary key AND the join key to a stored message's
   * `receiverEmail`. Nothing rewrites past messages, so changing it here leaves
   * every message that department has already received attributed to an address
   * that no longer exists — they still list, but under the bare address instead
   * of the department name. Said before the save, while it can still be undone.
   */
  const isRenaming =
    isEdit && values.email.trim().toLowerCase() !== setting.email.toLowerCase();

  const handleSubmit = (event) => {
    event?.preventDefault?.();
    setTouched(true);
    if (Object.keys(errors).length > 0) return;

    onSubmit({
      // Only meaningful on edit; the create hook ignores it.
      originalEmail: setting?.email,
      email: values.email.trim(),
      title: values.title.trim(),
      mobile: values.mobile.trim(),
      isEmailSentRequired: values.isEmailSentRequired,
      isActive: values.isActive,
    });
  };

  return (
    <Modal
      onClose={onClose}
      closeWhite={false}
      innerContainerClass={styles['form-modal']}
      modalInnerContainerStyle={{ padding: 0, gap: 0 }}
      // Hidden via the inline-style prop rather than CSS: `Modal.jsx` renders
      // the icon with a css-module class the bundler hashes, which no rule in
      // this file can match. `closeStyle` is spread onto the element's own
      // `style`, so it wins regardless of hashing.
      closeStyle={{ display: 'none' }}
    >
      <form
        className={styles.dialog}
        dir='rtl'
        onSubmit={handleSubmit}
        noValidate
        // The atom closes on a backdrop mousedown; a click inside the form must
        // not count as one, or a mis-drag while typing throws the entry away.
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles['dialog-head']}>
          <h2 className={styles['dialog-title']}>
            {isEdit ? 'تعديل جهة التواصل' : 'إضافة جهة تواصل'}
          </h2>
          <button
            type='button'
            className={styles['dialog-close']}
            onClick={onClose}
            aria-label='إغلاق'
          >
            <span aria-hidden='true'>&#10005;</span>
          </button>
        </header>

        <div className={styles['dialog-body']}>
          {/* Anything the server refused that is not tied to one field —
              including the 401 with an empty body, which carries no envelope to
              read a field name out of. */}
          {serverMessage && (
            <p className={styles['form-error']} role='alert'>
              {localise(serverMessage)}
            </p>
          )}

          <div className={styles.field}>
            <label className={styles['field-label']} htmlFor='cs-email'>
              البريد الإلكتروني
              <span className={styles.required} aria-hidden='true'>
                *
              </span>
            </label>
            <input
              id='cs-email'
              type='email'
              dir='ltr'
              className={`${styles.input} ${
                errorFor('email') ? styles['input-invalid'] : ''
              }`}
              value={values.email}
              onChange={(e) => setField({ email: e.target.value })}
              aria-invalid={!!errorFor('email')}
              aria-describedby={errorFor('email') ? 'cs-email-error' : undefined}
              autoComplete='off'
            />
            {errorFor('email') && (
              <p
                className={styles['field-error']}
                id='cs-email-error'
                role='alert'
              >
                {errorFor('email')}
              </p>
            )}
            {isRenaming && (
              <p className={styles['form-warning']} role='status'>
                تغيير البريد الإلكتروني لن يُحدّث الرسائل السابقة؛ الرسائل التي
                وصلت إلى العنوان القديم ستظهر باسم العنوان نفسه بدلًا من اسم
                الجهة.
              </p>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles['field-label']} htmlFor='cs-title'>
              اسم الجهة
              <span className={styles.required} aria-hidden='true'>
                *
              </span>
            </label>
            <input
              id='cs-title'
              type='text'
              className={`${styles.input} ${
                errorFor('title') ? styles['input-invalid'] : ''
              }`}
              value={values.title}
              onChange={(e) => setField({ title: e.target.value })}
              aria-invalid={!!errorFor('title')}
              aria-describedby={errorFor('title') ? 'cs-title-error' : undefined}
            />
            {errorFor('title') && (
              <p
                className={styles['field-error']}
                id='cs-title-error'
                role='alert'
              >
                {errorFor('title')}
              </p>
            )}
            <p className={styles['field-hint']}>
              الاسم الذي يظهر للزائر في قائمة الجهات على صفحة «تواصل معنا».
            </p>
          </div>

          <div className={styles.field}>
            <label className={styles['field-label']} htmlFor='cs-mobile'>
              رقم الهاتف
              <span className={styles.required} aria-hidden='true'>
                *
              </span>
            </label>
            <input
              id='cs-mobile'
              type='tel'
              dir='ltr'
              inputMode='tel'
              className={`${styles.input} ${
                errorFor('mobile') ? styles['input-invalid'] : ''
              }`}
              value={values.mobile}
              onChange={(e) => setField({ mobile: sanitiseMobile(e.target.value) })}
              aria-invalid={!!errorFor('mobile')}
              aria-describedby={
                errorFor('mobile') ? 'cs-mobile-error' : undefined
              }
              placeholder='+9714xxxxxxx'
              autoComplete='off'
            />
            {errorFor('mobile') && (
              <p
                className={styles['field-error']}
                id='cs-mobile-error'
                role='alert'
              >
                {errorFor('mobile')}
              </p>
            )}
            <p className={styles['field-hint']}>
              أرقام و«+» فقط — بدون مسافات أو رموز أخرى.
            </p>
          </div>

          <label className={styles['check-field']}>
            <input
              type='checkbox'
              className={styles.switch}
              checked={values.isEmailSentRequired}
              onChange={(e) =>
                setField({ isEmailSentRequired: e.target.checked })
              }
            />
            <span className={styles['check-text']}>
              <span className={styles['check-title']}>
                إرسال نسخة بالبريد إلى هذه الجهة
              </span>
              <span className={styles['field-hint']}>
                عند التفعيل تُرسل نسخة من رسالة الزائر إلى هذا البريد. وفي جميع
                الحالات تُحفظ الرسالة وتظهر في «رسائل التواصل».
              </span>
            </span>
          </label>

          <label className={styles['check-field']}>
            <input
              type='checkbox'
              className={styles.switch}
              checked={values.isActive}
              onChange={(e) => setField({ isActive: e.target.checked })}
            />
            <span className={styles['check-text']}>
              <span className={styles['check-title']}>جهة مُفعّلة</span>
              <span className={styles['field-hint']}>
                عطّل الجهة بدلًا من حذفها إذا كنت تنوي إعادتها لاحقًا — الحذف
                نهائي ولا يمكن التراجع عنه.
              </span>
            </span>
          </label>
        </div>

        <footer className={styles['dialog-foot']}>
          {isSaving ? (
            <LoaderButton />
          ) : (
            <>
              {/* `type="submit"` is not decoration: without it the `Buttons`
                  atom wraps its button in a react-router `<Link>`, which inside
                  a form both navigates and submits. The submit variant renders
                  a bare `<button type="submit">`, so the form's own `onSubmit`
                  is the single path in — and Enter saves from the keyboard.
                  Same pairing as `Molecules/Session/ReauthDialog`. */}
              <Buttons
                text={isEdit ? 'حفظ التعديلات' : 'إضافة'}
                rounded={true}
                color='rgba(13, 160, 110, 1)'
                type='submit'
                // `.aud-button-rounded` carries `width: 100%; flex: 1`, and in
                // a flex footer `flex: 1` beats the `width` prop outright —
                // which is why the primary action was swallowing the whole row
                // with the cancel squeezed beside it. `style` is spread last by
                // the atom, so overriding `flex` here is what actually lands.
                width={180}
                style={{ flex: '0 0 auto' }}
              />
              <button
                type='button'
                className={styles['ghost-button']}
                onClick={onClose}
              >
                إلغاء
              </button>
            </>
          )}
        </footer>
      </form>
    </Modal>
  );
};

export default ContactSettingFormModal;
